import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { fetchFavorites, toggleFavorite as persistFavorite } from '../api/memberApi';
import { drumUserTokenAtom } from '../store/drumAppAtoms';

export function useFavorites(type: 'score' | 'cognitive') {
  const token = useAtomValue(drumUserTokenAtom);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const pendingIds = useRef(new Set<string>());

  useEffect(() => {
    if (!token) {
      setFavoriteIds([]);
      return undefined;
    }
    let cancelled = false;
    fetchFavorites(token, type)
      .then((ids) => {
        if (!cancelled) setFavoriteIds(ids);
      })
      .catch(() => {
        if (!cancelled) setFavoriteIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, type]);

  const toggleFavorite = useCallback(async (id?: string) => {
    if (!id || pendingIds.current.has(id)) return;
    const wasLiked = favoriteIds.includes(id);
    setFavoriteIds((current) => (wasLiked ? current.filter((item) => item !== id) : [...current, id]));
    if (!token) return;
    pendingIds.current.add(id);
    try {
      const saved = await persistFavorite(token, type, id);
      setFavoriteIds(saved.ids);
    } catch {
      setFavoriteIds((current) => (wasLiked
        ? (current.includes(id) ? current : [...current, id])
        : current.filter((item) => item !== id)));
    } finally {
      pendingIds.current.delete(id);
    }
  }, [favoriteIds, token, type]);

  return { favoriteIds, toggleFavorite };
}
